import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import { useAuth } from '../../context/authContext';
import { getTravelProfile, toggleChecklistItem, updateTravelDetail } from '../../api/readyToFly';
import { updateApplication } from '../../api/academic';
import { listLookup } from '../../api/masterData';
import { uploadStudentDocument } from '../../api/students';
import { ChecklistItem, TravelDetailInput } from '../../types/readyToFly';

const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const VERIFICATION_COLOR: Record<string, 'default' | 'success' | 'error'> = {
  PENDING: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
};

interface TravelDetailDialogProps {
  applicationId: number | null;
  onClose: () => void;
}

export const TravelDetailDialog = ({ applicationId, onClose }: TravelDetailDialogProps) => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const open = applicationId !== null;
  const canManage = hasPermission('ready_to_fly', 'manage');
  const canEditDates = hasPermission('academic', 'update');
  const canUploadDocs = hasPermission('documents', 'upload');

  const [form, setForm] = useState<TravelDetailInput>({});
  const [departureDate, setDepartureDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');

  const profileQuery = useQuery({
    queryKey: ['travel-profile', applicationId],
    queryFn: () => getTravelProfile(applicationId!),
    enabled: open,
  });

  const documentTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/document-types'],
    queryFn: () => listLookup('/master-data/document-types', { activeOnly: true }),
    enabled: open && canUploadDocs,
  });

  useEffect(() => {
    if (!profileQuery.data) return;
    const detail = profileQuery.data.travelDetail;
    setForm({
      airline: detail.airline ?? '',
      flightNumber: detail.flightNumber ?? '',
      departureAirport: detail.departureAirport ?? '',
      arrivalAirport: detail.arrivalAirport ?? '',
      pickupRequired: detail.pickupRequired,
      pickupAssigned: detail.pickupAssigned,
      pickupAssignedTo: detail.pickupAssignedTo ?? '',
      pickupNotes: detail.pickupNotes ?? '',
      accommodationArranged: detail.accommodationArranged,
      accommodationDetails: detail.accommodationDetails ?? '',
      insuranceArranged: detail.insuranceArranged,
      insuranceProvider: detail.insuranceProvider ?? '',
      insurancePolicyNumber: detail.insurancePolicyNumber ?? '',
      insuranceExpiryDate: detail.insuranceExpiryDate ? detail.insuranceExpiryDate.slice(0, 10) : null,
      studentPackProvided: detail.studentPackProvided,
    });
    setDepartureDate(profileQuery.data.application.departureDate?.slice(0, 10) ?? '');
    setArrivalDate(profileQuery.data.application.arrivalDate?.slice(0, 10) ?? '');
  }, [profileQuery.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['travel-profile', applicationId] });
    queryClient.invalidateQueries({ queryKey: ['journey'] });
    queryClient.invalidateQueries({ queryKey: ['ready-to-fly-dashboard'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await updateTravelDetail(applicationId!, form);
      if (canEditDates) {
        await updateApplication(applicationId!, {
          departureDate: departureDate || null,
          arrivalDate: arrivalDate || null,
        });
      }
    },
    onSuccess: invalidate,
  });

  const checklistMutation = useMutation({
    mutationFn: ({ list, key, checked }: { list: 'departure' | 'arrival'; key: string; checked: boolean }) =>
      toggleChecklistItem(applicationId!, list, key, checked),
    onSuccess: invalidate,
  });

  const uploadTicketMutation = useMutation({
    mutationFn: async (file: File) => {
      const ticketType = documentTypesQuery.data?.find((t) => t.name === 'Air Ticket');
      if (!ticketType) throw new Error('Air Ticket document type is not configured');
      return uploadStudentDocument(profileQuery.data!.application.student.id, file, ticketType.id as number);
    },
    onSuccess: invalidate,
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const handleSave = () => {
    setSaveError(null);
    saveMutation.mutate(undefined, {
      onError: (err) =>
        setSaveError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save'),
    });
  };

  const renderChecklist = (title: string, items: ChecklistItem[], list: 'departure' | 'arrival') => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack>
        {items.map((item) => (
          <FormControlLabel
            key={item.key}
            control={
              <Checkbox
                size="small"
                checked={item.checked}
                disabled={!canManage || checklistMutation.isPending}
                onChange={(e) => checklistMutation.mutate({ list, key: item.key, checked: e.target.checked })}
              />
            }
            label={
              <Typography variant="body2">
                {item.label}
                {item.checked && item.checkedAt && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({new Date(item.checkedAt).toLocaleDateString()})
                  </Typography>
                )}
              </Typography>
            }
          />
        ))}
      </Stack>
    </Box>
  );

  const profile = profileQuery.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ready to Fly — Travel Profile</DialogTitle>
      <DialogContent>
        {profileQuery.isLoading && <CircularProgress size={24} />}
        {profile && (
          <Stack spacing={3} sx={{ pb: 1 }}>
            <Box>
              <Typography variant="subtitle1">
                {profile.application.student.firstName} {profile.application.student.lastName} (
                {profile.application.student.studentNumber})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profile.application.university.name}, {profile.application.university.country.name} —{' '}
                {profile.application.course.name}
              </Typography>
              <Chip size="small" sx={{ mt: 0.5 }} label={profile.application.status.replace(/_/g, ' ')} />
            </Box>

            <Alert
              icon={<FlightTakeoffIcon />}
              severity={profile.eligibility.eligible ? 'success' : 'warning'}
            >
              {profile.eligibility.eligible
                ? 'Eligible for Ready to Fly.'
                : profile.eligibility.reasons.join('; ')}
            </Alert>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Flight details
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Airline"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.airline ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, airline: e.target.value }))}
                  />
                  <TextField
                    label="Flight number"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.flightNumber ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, flightNumber: e.target.value }))}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Departure airport"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.departureAirport ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, departureAirport: e.target.value }))}
                  />
                  <TextField
                    label="Arrival airport"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.arrivalAirport ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, arrivalAirport: e.target.value }))}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Departure date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    disabled={!canEditDates}
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                  />
                  <TextField
                    label="Arrival date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    disabled={!canEditDates}
                    value={arrivalDate}
                    onChange={(e) => setArrivalDate(e.target.value)}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pickup
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.pickupRequired}
                        disabled={!canManage}
                        onChange={(e) => setForm((f) => ({ ...f, pickupRequired: e.target.checked }))}
                      />
                    }
                    label="Pickup required"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.pickupAssigned}
                        disabled={!canManage}
                        onChange={(e) => setForm((f) => ({ ...f, pickupAssigned: e.target.checked }))}
                      />
                    }
                    label="Pickup assigned"
                  />
                </Stack>
                <TextField
                  label="Assigned to (driver / vendor)"
                  fullWidth
                  size="small"
                  disabled={!canManage}
                  value={form.pickupAssignedTo ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, pickupAssignedTo: e.target.value }))}
                />
                <TextField
                  label="Pickup notes"
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  disabled={!canManage}
                  value={form.pickupNotes ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, pickupNotes: e.target.value }))}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Accommodation
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.accommodationArranged}
                      disabled={!canManage}
                      onChange={(e) => setForm((f) => ({ ...f, accommodationArranged: e.target.checked }))}
                    />
                  }
                  label="Accommodation arranged"
                />
                <TextField
                  label="Accommodation details"
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  disabled={!canManage}
                  value={form.accommodationDetails ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, accommodationDetails: e.target.value }))}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Insurance
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.insuranceArranged}
                      disabled={!canManage}
                      onChange={(e) => setForm((f) => ({ ...f, insuranceArranged: e.target.checked }))}
                    />
                  }
                  label="Insurance arranged"
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Provider"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.insuranceProvider ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, insuranceProvider: e.target.value }))}
                  />
                  <TextField
                    label="Policy number"
                    fullWidth
                    size="small"
                    disabled={!canManage}
                    value={form.insurancePolicyNumber ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, insurancePolicyNumber: e.target.value }))}
                  />
                </Stack>
                <TextField
                  label="Policy expiry"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!canManage}
                  value={form.insuranceExpiryDate ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, insuranceExpiryDate: e.target.value || null }))}
                />
              </Stack>
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.studentPackProvided}
                    disabled={!canManage}
                    onChange={(e) => setForm((f) => ({ ...f, studentPackProvided: e.target.checked }))}
                  />
                }
                label="Student pack provided"
              />
            </Box>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {canManage && (
              <Button variant="contained" disabled={saveMutation.isPending} onClick={handleSave} sx={{ alignSelf: 'flex-start' }}>
                {saveMutation.isPending ? 'Saving…' : 'Save travel details'}
              </Button>
            )}

            <Divider />

            {renderChecklist('Departure checklist', profile.travelDetail.departureChecklist, 'departure')}
            {renderChecklist('Arrival checklist', profile.travelDetail.arrivalChecklist, 'arrival')}

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Emergency contact
              </Typography>
              <Typography variant="body2">
                {profile.application.student.emergencyContactName ?? '—'} (
                {profile.application.student.emergencyContactRelationship ?? '—'}) —{' '}
                {profile.application.student.emergencyContactPhone ?? '—'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Passport, visa &amp; ticket
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                {['Passport', 'Visa', 'Air Ticket'].map((typeName) => {
                  const doc = profile.documents.find((d) => d.documentType.name === typeName);
                  return (
                    <Chip
                      key={typeName}
                      size="small"
                      label={doc ? `${typeName}: ${doc.verificationStatus}` : `${typeName}: not uploaded`}
                      color={doc ? VERIFICATION_COLOR[doc.verificationStatus] : 'default'}
                    />
                  );
                })}
              </Stack>
              {canUploadDocs && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadTicketMutation.mutate(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<UploadFileIcon />}
                    disabled={uploadTicketMutation.isPending}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadTicketMutation.isPending ? 'Uploading…' : 'Upload air ticket'}
                  </Button>
                </>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Passport #{profile.application.student.passportNumber ?? '—'} · Expires{' '}
              {formatDate(profile.application.student.passportExpiryDate)}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
